// External packages
import { Controller, useFormContext, useWatch } from 'react-hook-form';

// Note: @Mateo I didnt know  how to pass place to this component.
import { useGetPlaces } from 'api/place';

// Components
import { Label, SubLabel } from 'components/Forms';
import { SvgEditor } from 'components/SvgEditor';

// Utils
import { twMerge } from 'tailwind-merge';

/* SelectCoordinatesMap */

type SelectCoordinatesMapProps = {
  value?: { x: number; y: number } | null;
  onChange?: (value: { x: number; y: number }) => void;
  isInvalid?: boolean;
  className?: string;
} & React.ComponentPropsWithRef<'div'>;

export const SelectCoordinatesMap: React.FC<SelectCoordinatesMapProps> = ({
  value,
  onChange,
  isInvalid,
  className,
  ...props
}) => {
  const { data: places = [] } = useGetPlaces();
  const { control } = useFormContext();
  const placeId = useWatch({ control, name: 'placeId' });

  const mapUrl = places.find((place) => place.id === placeId)?.map?.url;

  const onPositionSelect = (position: { x: number; y: number }) => {
    onChange?.(position);
  };

  if (!mapUrl) {
    return (
      <div
        {...props}
        className={twMerge(
          'relative flex aspect-video w-full items-center justify-center rounded-lg border border-gray-200 bg-white',
          isInvalid && 'border-red-500',
          className
        )}
      >
        <p className="text-gray-500">No map available</p>
      </div>
    );
  }

  return (
    <SvgEditor
      url={mapUrl}
      altText="Coordinate Selection Map"
      width={800}
      height={600}
      className={className}
      selectedPosition={value}
      onPositionSelect={onPositionSelect}
      isInvalid={isInvalid}
    />
  );
};

type SelectCoordinatesMapFieldProps = {
  name: string;
  isInvalid?: boolean;
  wrapperClassName?: string;
  className?: string;
  label?: string;
  isRequired?: boolean;
} & Omit<SelectCoordinatesMapProps, 'value' | 'onChange'>;

export const SelectCoordinatesMapField: React.FC<
  SelectCoordinatesMapFieldProps
> = ({
  name,
  isInvalid,
  label,
  wrapperClassName,
  isRequired,
  className,
  ...props
}) => {
  const { control } = useFormContext();
  const placeId = useWatch({ control, name: 'placeId' });

  if (!placeId) return null;

  return (
    <Controller
      name={name}
      render={({ field, fieldState }) => (
        <div className={wrapperClassName}>
          {label && (
            <Label
              htmlFor={name}
              className={twMerge(
                isRequired &&
                  'after:content-["*"] after:ml-1 after:text-red-600'
              )}
            >
              {label}
            </Label>
          )}
          <SelectCoordinatesMap
            {...props}
            value={field.value}
            onChange={field.onChange}
            isInvalid={fieldState.invalid}
            className={className}
          />
          {fieldState.error && (
            <SubLabel role="alert" slot="errorMessage" colorScheme="error">
              {fieldState.error.message}
            </SubLabel>
          )}
        </div>
      )}
    />
  );
};
